/**
 * PageRenderer — renders a published Page document as a public page.
 * Loads brand-consistent header/footer via NavigationMenus loaded from DB.
 * Uses react-helmet-async to inject per-page SEO meta.
 */
import React, { useEffect, useState } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { homeVariant, setHomeVariant, trackHome } from "@/lib/requestContext";
import api from "@/lib/api";
import { SectionRenderer, BLOCK_REGISTRY } from "@/builder/blockRegistry";
import PublicLayout from "@/layouts/PublicLayout";
import { IS_ES, withLang } from "@/lib/i18n/lang";

export default function PageRenderer() {
  const location = useLocation();
  const params = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    let alive = true;
    setPage(null);
    setError(null);
    // Try exact route
    const routeFor = async (path) => {
      if (path === "/home-marketplace") return "/";
      if (path !== "/") return path;
      try { const { data } = await api.get("/homepage/active"); return data.active === "request" ? "/home-request" : "/"; } catch (_) { return "/"; }
    };
    routeFor(location.pathname).then((route) => api.get("/pages/by-route", { params: { route } }))
      .then((r) => {
        if (!alive) return;
        setPage(r.data);
        if (r.data.slug === "home-request" || r.data.slug === "home") {
          const v = r.data.slug === "home-request" ? "request" : "marketplace";
          const fresh = homeVariant() !== v;
          setHomeVariant(v);
          if (fresh) trackHome("session");
        }
      })
      .catch(async () => {
        // Fallback: try by-slug for /:slug patterns
        const seg = location.pathname.replace(/^\//, "").split("/")[0];
        if (seg) {
          try {
            const r = await api.get(`/pages/by-slug/${seg}`);
            if (alive) setPage(r.data);
            return;
          } catch {}
        }
        // Look for redirect rule
        try {
          const rd = await api.get("/redirects/resolve", { params: { path: location.pathname } });
          if (alive && rd.data?.match) {
            setRedirectTo(rd.data.destination);
            return;
          }
        } catch {}
        if (alive) setError("Page not found");
      });
    return () => { alive = false; };
  }, [location.pathname]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  if (error) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto py-24 text-center px-4">
          <div className="text-xs uppercase tracking-widest text-[#165a71]">404</div>
          <h1 className="mt-2 font-display text-4xl font-semibold text-[#071b22]">This page doesn't exist</h1>
          <p className="mt-3 text-[#252A34]/70">Try the search bar or head back to the homepage.</p>
        </div>
      </PublicLayout>
    );
  }

  if (!page) {
    return <PublicLayout><div className="max-w-6xl mx-auto py-24 px-4 animate-pulse text-[#252A34]/60 text-sm">Loading page…</div></PublicLayout>;
  }

  return (
    <PublicLayout menuKey={page.header_menu_id || undefined}>
      <Helmet>
        <title>{page.seo_title || page.title}</title>
        {page.seo_description && <meta name="description" content={page.seo_description} />}
        {(page.seo_keywords || []).length > 0 && <meta name="keywords" content={(page.seo_keywords || []).join(", ")} />}
        {page.is_home ? <link rel="canonical" href={`${window.location.origin}${IS_ES ? "/es" : "/"}`} /> : page.canonical_url && <link rel="canonical" href={IS_ES && page.canonical_url.startsWith("/") ? withLang("es", page.canonical_url) : page.canonical_url} />}
        <meta name="robots" content={`${page.robots_index ? "index" : "noindex"},${page.robots_follow ? "follow" : "nofollow"}`} />
        <meta property="og:title" content={page.seo_title || page.title} />
        {page.seo_description && <meta property="og:description" content={page.seo_description} />}
        {page.seo_image_url && <meta property="og:image" content={page.seo_image_url.startsWith("/") ? window.location.origin + page.seo_image_url : page.seo_image_url} />}
        <meta property="og:type" content="website" />
        {page.schema_json && <script type="application/ld+json">{JSON.stringify(page.schema_json)}</script>}
        {page.custom_css && <style>{page.custom_css}</style>}
      </Helmet>
      <div data-testid={`page-render-${page.slug}`} className={(page.sections || []).slice(-1)[0]?.blocks?.some((b) => BLOCK_REGISTRY[b.kind]?.fullBleed) ? "-mb-24" : ""}>
        {(page.sections || []).map((s) => (
          <SectionRenderer key={s.id} section={s} mode="view" />
        ))}
      </div>
    </PublicLayout>
  );
}
